<template>
  <div class="mypage">
    <div class="mypage-inner">
      <!-- 프로필 섹션 -->
      <section class="toss-section profile-section">
        <div v-if="!isLoggedIn" class="guest-view">
          <h2 class="toss-title">로그인이 필요해요</h2>
          <p class="toss-desc">물개와 함께 분양 현장을 찾아보세요</p>
          <router-link to="/login" class="toss-btn-primary">
            로그인 / 회원가입
          </router-link>
        </div>

        <div v-else class="user-view">
          <div class="user-header">
            <h2 class="user-name">
              {{ userProfile?.name || '회원' }}님
            </h2>
            <router-link to="/mypage/settings" class="btn-text">설정</router-link>
          </div>
          <p class="user-email">{{ userProfile?.email }}</p>
        </div>
      </section>

      <!-- 물개 포인트 섹션 -->
      <section v-if="isLoggedIn" class="point-section" @click="goTo('points')">
        <div class="point-card">
          <div class="point-header">
            <div class="point-label">
              <span class="point-icon">🐟</span>
              <span>물개 포인트</span>
            </div>
            <svg class="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </div>
          <div class="point-balance">
            <span class="balance-value">{{ pointBalance.toLocaleString() }}</span>
            <span class="balance-unit">P</span>
          </div>
          <div class="point-actions">
            <button class="point-action-btn" @click.stop="goTo('checkin')">
              <span class="action-icon">📅</span>
              <span>출석체크</span>
            </button>
            <button class="point-action-btn" @click.stop="goTo('shop')">
              <span class="action-icon">🛒</span>
              <span>상품 샵</span>
            </button>
            <button class="point-action-btn" @click.stop="goTo('history')">
              <span class="action-icon">📋</span>
              <span>포인트 내역</span>
            </button>
          </div>
        </div>
      </section>

      <!-- 인앱 광고 배너 -->
      <section v-if="adBanner" class="ad-banner-section" @click="handleAdClick">
        <div class="ad-badge">{{ adBanner.badge || 'AD' }}</div>
        <div class="ad-content">
          <div class="ad-image">
            <img v-if="adBanner.imageUrl" :src="adBanner.imageUrl" :alt="adBanner.title" />
            <div v-else class="ad-image-placeholder">
              <span>🏠</span>
            </div>
          </div>
          <div class="ad-info">
            <span v-if="adBanner.propertyName" class="ad-region">{{ adBanner.propertyName }}</span>
            <h4 class="ad-title">{{ adBanner.title }}</h4>
            <p class="ad-cta">{{ adBanner.ctaText || '자세히 보기' }} →</p>
          </div>
        </div>
      </section>

      <!-- 퀵 메뉴 그리드 (4개) -->
      <section class="toss-section grid-section">
        <h3 class="section-label">자주 찾는 메뉴</h3>
        <div class="menu-grid">
          <button class="grid-item" @click="handleNavClick('favorites')">
            <div class="grid-icon icon-3d pink">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
              </svg>
            </div>
            <span class="grid-label">찜한 현장</span>
          </button>

          <button class="grid-item" @click="goTo('list')">
            <div class="grid-icon icon-3d green">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
              </svg>
            </div>
            <span class="grid-label">현장 목록</span>
          </button>

          <button class="grid-item" @click="goTo('event')">
            <div class="grid-icon icon-3d yellow">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
              </svg>
            </div>
            <span class="grid-label">이벤트</span>
          </button>

          <button class="grid-item" @click="goTo('support')">
            <div class="grid-icon icon-3d blue">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 11h2a2 2 0 012 2v3a2 2 0 01-2 2H3v-7z"/>
                <path d="M21 11h-2a2 2 0 00-2 2v3a2 2 0 002 2h2v-7z"/>
                <path d="M3 11V9a9 9 0 1118 0v2" fill="none" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
            <span class="grid-label">고객센터</span>
          </button>
        </div>
      </section>

      <!-- 관심 현장 섹션 -->
      <section class="toss-section">
        <h3 class="section-label">관심 현장</h3>

        <button class="toss-list-item" @click="handleNavClick('favorites')">
          <div class="item-left">
            <span class="item-icon">❤️</span>
            <span class="item-text">찜한 현장</span>
          </div>
          <div class="item-right">
            <span v-if="isLoggedIn" class="item-count">{{ favoriteCount }}</span>
            <svg class="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </div>
        </button>

        <button class="toss-list-item" @click="goTo('recent')">
          <div class="item-left">
            <span class="item-icon">👀</span>
            <span class="item-text">최근 본 현장</span>
          </div>
          <div class="item-right">
            <span class="item-count">{{ recentCount }}</span>
            <svg class="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </div>
        </button>

        <button class="toss-list-item" @click="handleNavClick('interests')">
          <div class="item-left">
            <span class="item-icon">📋</span>
            <span class="item-text">관심 등록한 현장</span>
          </div>
          <div class="item-right">
            <span v-if="isLoggedIn" class="item-count">{{ interestCount }}</span>
            <svg class="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </div>
        </button>
      </section>

      <!-- 커뮤니티 활동 섹션 -->
      <section v-if="isLoggedIn" class="toss-section">
        <h3 class="section-label">커뮤니티 활동</h3>

        <button class="toss-list-item" @click="goTo('myPosts')">
          <div class="item-left">
            <span class="item-icon">📝</span>
            <span class="item-text">내가 쓴 글</span>
          </div>
          <div class="item-right">
            <span class="item-count">{{ myPostCount }}</span>
            <svg class="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </div>
        </button>

        <button class="toss-list-item" @click="goTo('myComments')">
          <div class="item-left">
            <span class="item-icon">💬</span>
            <span class="item-text">내가 쓴 댓글</span>
          </div>
          <div class="item-right">
            <span class="item-count">{{ myCommentCount }}</span>
            <svg class="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </div>
        </button>

        <button class="toss-list-item" @click="goTo('likedPosts')">
          <div class="item-left">
            <span class="item-icon">❤️</span>
            <span class="item-text">좋아요한 글</span>
          </div>
          <div class="item-right">
            <span class="item-count">{{ likedPostCount }}</span>
            <svg class="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </div>
        </button>

        <button class="toss-list-item" @click="goTo('bookmarks')">
          <div class="item-left">
            <span class="item-icon">🔖</span>
            <span class="item-text">북마크한 글</span>
          </div>
          <div class="item-right">
            <span class="item-count">{{ bookmarkCount }}</span>
            <svg class="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </div>
        </button>
      </section>

      <!-- 서비스 섹션 -->
      <section class="toss-section">
        <h3 class="section-label">서비스</h3>

        <button class="toss-list-item" @click="goTo('list')">
          <div class="item-left">
            <span class="item-icon">🏘️</span>
            <span class="item-text">전체 현장 목록</span>
          </div>
          <svg class="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>

        <button class="toss-list-item" @click="goTo('event')">
          <div class="item-left">
            <span class="item-icon">🎉</span>
            <span class="item-text">이벤트</span>
          </div>
          <svg class="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>

        <button class="toss-list-item" @click="goTo('support')">
          <div class="item-left">
            <span class="item-icon">🎧</span>
            <span class="item-text">고객센터</span>
          </div>
          <svg class="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      </section>

      <!-- 설정 섹션 -->
      <section class="toss-section">
        <h3 class="section-label">설정</h3>

        <button class="toss-list-item" @click="goTo('notices')">
          <div class="item-left">
            <span class="item-icon">📢</span>
            <span class="item-text">공지사항</span>
          </div>
          <svg class="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>

        <button class="toss-list-item" @click="handleNavClick('settings')">
          <div class="item-left">
            <span class="item-icon">⚙️</span>
            <span class="item-text">계정 설정</span>
          </div>
          <svg class="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>

        <button v-if="isLoggedIn" class="toss-list-item logout" @click="handleLogout">
          <span class="item-text">로그아웃</span>
        </button>
      </section>

      <!-- 하단 여백 (BottomNav 고려) -->
      <div class="bottom-spacer"></div>
    </div>

    <!-- 토스트 메시지 -->
    <transition name="toast">
      <div v-if="showToast" class="toast">{{ toastMessage }}</div>
    </transition>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '@/composables/common/useAuth'
import { useRecent } from '@/composables/main/useRecent'
import { useFavorite } from '@/composables/main/useFavorite'
import { useInterest } from '@/composables/main/useInterest'
import { useAdBanner } from '@/composables/main/useAdBanner'
import { usePoint } from '@/composables/main/usePoint'
import { useCommunity } from '@/composables/main/useCommunity'

export default {
  name: 'MypageView',
  setup() {
    const router = useRouter()
    const { userProfile, isLoggedIn, logout } = useAuth()
    const { getRecentCount } = useRecent()
    const { getFavoriteCount } = useFavorite()
    const { getInterestCount } = useInterest()
    const { getRandomBanner, incrementClick, incrementImpression } = useAdBanner()
    const { getUserPoints } = usePoint()
    const { getMyPosts, getMyComments, getMyLikedPosts, getMyBookmarks } = useCommunity()

    // 카운트
    const recentCount = ref(0)
    const favoriteCount = ref(0)
    const interestCount = ref(0)

    // 커뮤니티 활동 카운트
    const myPostCount = ref(0)
    const myCommentCount = ref(0)
    const likedPostCount = ref(0)
    const bookmarkCount = ref(0)

    // 포인트 잔액
    const pointBalance = ref(0)

    // 광고 배너
    const adBanner = ref(null)

    // 토스트
    const showToast = ref(false)
    const toastMessage = ref('')

    // 카운트 로드
    const loadCounts = async () => {
      recentCount.value = getRecentCount()

      if (isLoggedIn.value) {
        favoriteCount.value = await getFavoriteCount()
        interestCount.value = await getInterestCount()

        // 포인트 잔액 로드
        try {
          const pointData = await getUserPoints()
          pointBalance.value = pointData?.balance || 0
        } catch (err) {
          console.error('포인트 로드 실패:', err)
          pointBalance.value = 0
        }

        // 커뮤니티 활동 카운트 로드
        try {
          const posts = await getMyPosts(userProfile.value?.uid || '', 100)
          myPostCount.value = posts.length

          const comments = await getMyComments(userProfile.value?.uid || '', 100)
          myCommentCount.value = comments.length

          const likedPosts = await getMyLikedPosts(userProfile.value?.uid || '', 100)
          likedPostCount.value = likedPosts.length

          const bookmarks = await getMyBookmarks(userProfile.value?.uid || '', 100)
          bookmarkCount.value = bookmarks.length
        } catch (err) {
          console.error('커뮤니티 활동 로드 실패:', err)
        }
      }
    }

    // 광고 배너 로드
    const loadAdBanner = async () => {
      const banner = await getRandomBanner()
      if (banner) {
        adBanner.value = banner
        // 노출 수 증가
        await incrementImpression(banner.id)
      }
    }

    // 광고 클릭 핸들러
    const handleAdClick = async () => {
      if (!adBanner.value) return

      // 클릭 수 증가
      await incrementClick(adBanner.value.id)

      // 캠페인 랜딩 페이지로 이동 (from=adbanner 파라미터 추가)
      if (adBanner.value.campaignCode) {
        router.push(`/p/${adBanner.value.campaignCode}?from=adbanner`)
      }
    }

    // 로그인 필요한 페이지 이동
    const handleNavClick = (page) => {
      if (!isLoggedIn.value) {
        router.push('/login')
        return
      }
      goTo(page)
    }

    // 페이지 이동
    const goTo = (page) => {
      const routes = {
        'list': '/properties',
        'favorites': '/mypage/favorites',
        'recent': '/mypage/recent',
        'interests': '/mypage/interests',
        'settings': '/mypage/settings',
        'notices': '/mypage/notices',
        'event': '/mypage/events',
        'support': '/mypage/support',
        'points': '/mypage/points',
        'checkin': '/mypage/points/checkin',
        'shop': '/mypage/points/shop',
        'history': '/mypage/points/history',
        // 커뮤니티 활동
        'myPosts': '/mypage/posts',
        'myComments': '/mypage/comments',
        'likedPosts': '/mypage/liked-posts',
        'bookmarks': '/mypage/bookmarks'
      }

      if (routes[page]) {
        router.push(routes[page])
      } else {
        showToastMessage('준비중입니다!')
      }
    }

    // 로그아웃
    const handleLogout = async () => {
      await logout()
      showToastMessage('로그아웃되었습니다.')
    }

    // 토스트 메시지
    const showToastMessage = (message) => {
      toastMessage.value = message
      showToast.value = true
      setTimeout(() => {
        showToast.value = false
      }, 2000)
    }

    onMounted(() => {
      loadCounts()
      loadAdBanner()
    })

    return {
      userProfile,
      isLoggedIn,
      recentCount,
      favoriteCount,
      interestCount,
      myPostCount,
      myCommentCount,
      likedPostCount,
      bookmarkCount,
      pointBalance,
      adBanner,
      showToast,
      toastMessage,
      handleNavClick,
      goTo,
      handleLogout,
      handleAdClick
    }
  }
}
</script>

<style scoped>
/* ========================================
   토스(Toss) 스타일 UI 디자인 시스템
   ======================================== */
.mypage {
  min-height: 100vh;
  background-color: #ffffff;
  padding-bottom: 80px;
}

.mypage-inner {
  max-width: 480px;
  margin: 0 auto;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* 섹션 공통 */
.toss-section {
  background-color: white;
  border-radius: 24px;
  padding: 24px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.section-label {
  font-size: 19px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 16px;
  padding-left: 4px;
}

/* ========================================
   1. 프로필 섹션
   ======================================== */
.profile-section {
  padding-bottom: 20px;
}

.guest-view {
  text-align: center;
  padding: 10px 0;
}

.toss-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.toss-desc {
  font-size: 15px;
  color: #64748b;
  margin-bottom: 24px;
}

.toss-btn-primary {
  display: block;
  width: 100%;
  padding: 16px;
  background-color: #2563eb;
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.toss-btn-primary:hover {
  background-color: #1d4ed8;
}

.user-view {
  padding: 8px 0;
}

.user-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.user-name {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
}

.user-email {
  font-size: 14px;
  color: #64748b;
}

.btn-text {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
  padding: 8px;
  cursor: pointer;
  text-decoration: none;
}

.btn-text:hover {
  color: #64748b;
}

/* ========================================
   2. 퀵 메뉴 (3D 스타일)
   ======================================== */
.grid-section {
  padding: 24px 16px;
}

.menu-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px 12px;
}

.grid-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  cursor: pointer;
}

.grid-icon {
  width: 56px;
  height: 56px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  transition: transform 0.15s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08), inset 0 2px 4px rgba(255, 255, 255, 0.4);
}

.grid-icon:active {
  transform: scale(0.92);
}

/* 각 아이콘별 3D 그라데이션 컬러 */
.grid-icon.icon-3d.pink {
  background: linear-gradient(135deg, #F9A8D4, #DB2777);
  color: white;
}

.grid-icon.icon-3d.blue {
  background: linear-gradient(135deg, #93C5FD, #2563EB);
  color: white;
}

.grid-icon.icon-3d.green {
  background: linear-gradient(135deg, #6EE7B7, #059669);
  color: white;
}

.grid-icon.icon-3d.yellow {
  background: linear-gradient(135deg, #FCD34D, #D97706);
  color: white;
}

/* 내부 SVG 아이콘에 살짝 그림자 줘서 붕 뜬 느낌 */
.grid-icon svg {
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.15));
}

.grid-label {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.2px;
}

/* ========================================
   3. 리스트 아이템 공통
   ======================================== */
.toss-list-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: transparent;
  border: none;
  padding: 16px 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-radius: 12px;
}

.toss-list-item:active {
  background-color: #f1f5f9;
}

.toss-list-item.logout .item-text {
  color: #94a3b8;
}

.item-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.item-icon {
  font-size: 24px;
  width: 24px;
  text-align: center;
}

.item-text {
  font-size: 17px;
  font-weight: 500;
  color: #1e293b;
}

.item-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-count {
  font-size: 15px;
  font-weight: 600;
  color: #94a3b8;
}

.arrow-icon {
  color: #cbd5e1;
}

/* 하단 여백 */
.bottom-spacer {
  height: 20px;
}

/* ========================================
   인앱 광고 배너
   ======================================== */
.ad-banner-section {
  position: relative;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 20px;
  padding: 16px;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.ad-banner-section:active {
  transform: scale(0.98);
}

.ad-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 10px;
  font-weight: 700;
  border-radius: 4px;
  letter-spacing: 0.5px;
}

.ad-content {
  display: flex;
  gap: 14px;
  align-items: center;
}

.ad-image {
  width: 80px;
  height: 80px;
  border-radius: 14px;
  overflow: hidden;
  flex-shrink: 0;
  background: white;
}

.ad-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ad-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  background: #f1f5f9;
}

.ad-info {
  flex: 1;
  min-width: 0;
}

.ad-region {
  display: inline-block;
  padding: 3px 8px;
  background: #2563eb;
  color: white;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  margin-bottom: 6px;
}

.ad-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ad-cta {
  font-size: 13px;
  font-weight: 600;
  color: #2563eb;
  margin-top: 4px;
}

/* ========================================
   토스트 메시지
   ======================================== */
.toast {
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(51, 51, 51, 0.95);
  color: white;
  padding: 12px 24px;
  border-radius: 100px;
  font-size: 14px;
  z-index: 1000;
  white-space: nowrap;
  font-weight: 500;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

/* ========================================
   물개 포인트 섹션
   ======================================== */
.point-section {
  cursor: pointer;
}

.point-card {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  border-radius: 24px;
  padding: 24px;
  color: white;
  transition: transform 0.2s;
}

.point-card:active {
  transform: scale(0.98);
}

.point-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.point-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  opacity: 0.9;
}

.point-icon {
  font-size: 20px;
}

.point-section .arrow-icon {
  color: rgba(255, 255, 255, 0.7);
}

.point-balance {
  margin-bottom: 20px;
}

.balance-value {
  font-size: 36px;
  font-weight: 800;
  letter-spacing: -1px;
}

.balance-unit {
  font-size: 20px;
  font-weight: 700;
  margin-left: 4px;
  opacity: 0.9;
}

.point-actions {
  display: flex;
  gap: 8px;
}

.point-action-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 8px;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 14px;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
}

.point-action-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

.point-action-btn:active {
  background: rgba(255, 255, 255, 0.1);
}

.action-icon {
  font-size: 20px;
}

.point-action-btn span:last-child {
  font-size: 12px;
  font-weight: 600;
}

/* 반응형 */
@media (max-width: 400px) {
  .menu-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px 16px;
  }
}
</style>
